import React, { useEffect, useState } from "react";
import {
  FilePdfOutlined,
  InboxOutlined,
  LogoutOutlined,
  UsergroupAddOutlined,
  ProductOutlined,
  DeploymentUnitOutlined,
  MailOutlined,
  LockOutlined,
  KeyOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import {
  Badge,
  Button,
  Layout,
  Menu,
  //theme
} from "antd";
import { RouterProvider } from "react-router";
import { router } from "./routing/router";
import { AuthService } from "./services/auth/auth_service";
import ThemeToggle from "./components/theme_toggle";
import { useThemeStore } from "shared-lib";
import TenantSwitcher from "./components/tenant-switcher";
import NewVersionBanner from "./components/new_version_banner";
import VersionBadge from "./components/version_badge";
import { useHasFeature } from "./state/tenant_state";
import { feedbackReviewNavEntry, visibleFeatureNavEntries, type ManagementNavEntry } from "./feature_navigation";
import { useFeedbackReviewCount, useFeedbackRoutingOptions } from "./hooks/useAppFeedbackReview";
import type { TenantFeatures } from "breuer-supercharged-sdk";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { cn } from "@/lib/utils";
import FeedbackButton from "./components/feedback/feedback_button";

const { Content, Sider } = Layout;

const FEATURE_ICONS: Record<string, React.ReactNode> = {
  pdf_scanner_v4: <ThunderboltOutlined />,
  offer_generation: <FilePdfOutlined />,
  steel_mind: <DeploymentUnitOutlined />,
  order_inbox: <InboxOutlined />,
  arbeitsvorbereitung: <ToolOutlined />,
};

const getMenuItems = (
  hasFeature: (feature: TenantFeatures) => boolean,
  collapsed: boolean,
  feedbackReviewEntry: ManagementNavEntry | null
): MenuProps["items"] => {
  const featureItems = visibleFeatureNavEntries(hasFeature).map((entry) => ({
    key: entry.key,
    icon: FEATURE_ICONS[entry.key],
    label: entry.label,
    onClick: () => router.navigate(entry.path, { viewTransition: true }),
  }));

  const menuItems: ItemType<MenuItemType>[] = [];

  // Add features group if there are any enabled features
  if (featureItems.length > 0) {
    menuItems.push({
      key: "features",
      type: "group",
      label: collapsed ? undefined : "Funktionen",
      children: featureItems,
    });
  }

  // Always add core data section
  menuItems.push({
    key: "core_data",
    type: "group",
    label: collapsed ? undefined : "Stammdaten",
    children: [
      {
        key: "customers",
        icon: <UsergroupAddOutlined />,
        label: `Kunden`,
        onClick: () => router.navigate("/customers", { viewTransition: true }),
      },
      {
        key: "containers",
        icon: <AppstoreOutlined />,
        label: `Baustellencontainer`,
        onClick: () => router.navigate("/containers", { viewTransition: true }),
      },
      {
        key: "products",
        icon: <ProductOutlined />,
        label: `Produkte`,
        onClick: () => router.navigate("/products", { viewTransition: true }),
      },
      {
        key: "email_settings",
        icon: <MailOutlined />,
        label: `Email Einstellungen`,
        onClick: () => router.navigate("/email_settings?view=templates", { viewTransition: true }),
      },
    ],
  });

  // Always add management section
  menuItems.push({
    key: "management",
    type: "group",
    label: collapsed ? undefined : "Verwaltung",
    children: [
      {
        key: "user-management",
        icon: <LockOutlined />,
        label: `Benutzer`,
        onClick: () => router.navigate("/user-management", { viewTransition: true }),
      },
      {
        key: "secrets-management",
        icon: <KeyOutlined />,
        label: `Variablen & Secrets`,
        onClick: () => router.navigate("/secrets-management", { viewTransition: true }),
      },
      ...(feedbackReviewEntry
        ? [
            {
              key: feedbackReviewEntry.key,
              icon: (
                <Badge
                  count={feedbackReviewEntry.badge ?? 0}
                  size="small"
                  offset={collapsed ? [2, -2] : [-2, -6]}
                  overflowCount={99}
                >
                  <CommentOutlined />
                </Badge>
              ),
              label: feedbackReviewEntry.label,
              onClick: () => router.navigate(feedbackReviewEntry.path, { viewTransition: true }),
            },
          ]
        : []),
    ],
  });

  return menuItems;
};

const DashboardShell: React.FC = () => {
  const location = window.location;
  const hasFeature = useHasFeature();
  const themeState = useThemeStore();
  const [collapsed, setCollapsed] = React.useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    const unsubscribe = router.subscribe((state) => {
      setIsNavigating(state.navigation.state === "loading");
    });
    return () => unsubscribe();
  }, []);

  // Zähler nur für Prüfer abfragen (sonst 403 im Minutentakt).
  const { data: routingOptions } = useFeedbackRoutingOptions();
  const isFeedbackReviewer = routingOptions?.currentUserIsReviewer ?? false;
  const { data: reviewCount } = useFeedbackReviewCount(isFeedbackReviewer);
  const feedbackReviewEntry = feedbackReviewNavEntry(isFeedbackReviewer, reviewCount?.pending ?? 0);

  const menuItems = getMenuItems(hasFeature, collapsed, feedbackReviewEntry);

  return (
    <Layout hasSider style={{ minHeight: "100vh" }}>
      {/* Navigation loading indicator */}
      <div className="fixed top-0 left-0 right-0 h-0.5 overflow-hidden z-1001">
        <div
          className={cn(
            "h-full transition-transform duration-300 ease-out",
            isNavigating
              ? "animate-[loading-bar_1.5s_ease-in-out_infinite]"
              : "-translate-x-full"
          )}
          style={{ backgroundColor: "#ffa200" }}
        />
      </div>
      {/* Proactive "a newer version is deployed" bar (offset by sider width). */}
      <NewVersionBanner collapsed={collapsed} />
      <Sider
        breakpoint="lg"
        collapsedWidth="50px"
        onBreakpoint={(broken) => {
          setCollapsed(broken);
        }}
        onCollapse={(collapsed) => {
          setCollapsed(collapsed);
        }}
        style={{
          overflow: "auto",
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
        }}
        theme={themeState.theme}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            paddingTop: 15,
            height: "100%",
            paddingBottom: 15,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                marginBottom: 15,
                flex: 0,
              }}
            >
              {collapsed ? (
                <img src="/assets/logo_icon.png" width={40} />
              ) : (
                <img src="/assets/logo_full.png" width={150} />
              )}
            </div>
            <Menu
              theme={themeState.theme}
              mode="vertical"
              items={menuItems}
              defaultSelectedKeys={[location.pathname.split("/")[1]]}
              inlineCollapsed={collapsed}
            />
          </div>
          <div>
            <div className="flex justify-center mb-3">
              <ThemeToggle collapsed={collapsed} />
            </div>
            <TenantSwitcher collapsed={collapsed} />
            <LogoutButton collapsed={collapsed} />
            <VersionBadge collapsed={collapsed} />
          </div>
        </div>
      </Sider>
      <Content
        style={{
          marginLeft: collapsed ? 50 : 200,
          padding: 24,
          overflow: "initial",
          minHeight: "100vh",
        }}
      >
        <RouterProvider router={router} />
      </Content>
      {/* Global feedback entry point — available on every screen. */}
      <FeedbackButton />
    </Layout>
  );
};

function LogoutButton({ collapsed }: { collapsed: boolean }) {
  async function handleLogout() {
    const result = confirm("Möchten Sie sich wirklich abmelden?");
    if (result) {
      await AuthService.getInstance().signOut();
    }
  }

  if (collapsed) {
    return (
      <Button
        danger
        style={{
          marginLeft: "9px",
        }}
        type="text"
        icon={<LogoutOutlined />}
        onClick={handleLogout}
      />
    );
  }
  return (
    <Button
      danger
      style={{
        margin: "0px 15px",
        width: "calc(100% - 30px)",
      }}
      type="text"
      icon={<LogoutOutlined />}
      onClick={handleLogout}
    >
      Abmelden
    </Button>
  );
}

export default DashboardShell;
